/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useState } from 'react';

import { jsx } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { ButtonItem, LinkItem, MenuGroup, Section } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import Popup from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Pressable, xcss } from '@atlaskit/primitives';

import { ANALYTIC_TYPE, ANALYTICS_CHANNEL, EVENT_TYPE } from '../../../utils/analytics';

const moreIconStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	marginRight: 'space.100',
	marginTop: 'space.100',
	borderRadius: 'radius.small',
	paddingRight: 'space.050',
	paddingLeft: 'space.050',
	paddingTop: 'space.025',
	paddingBottom: 'space.025',
	display: 'flex',
	justifyContent: 'center',
	':hover': {
		cursor: 'pointer',
		backgroundColor: 'color.background.neutral.hovered',
		textDecoration: 'inherit',
		transitionDuration: '0s, .15s',
	},
});

const contentStyles = xcss({
	padding: 'space.150',
});

export const MoreIconPopup = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isOpen, setIsOpen] = useState(false);
	const [isOverrideComplete, setIsOverrideComplete] = useState(false);
	const [flags, setFlags] = useState<Array<string>>([]);

	const handleButtonClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'optedOut',
			actionSubject: 'sxsOptionsMenu',
			type: ANALYTIC_TYPE.SEND_TRACK_EVENT,
			eventType: EVENT_TYPE.TRACK,
			source: ANALYTICS_CHANNEL,
		});
		setFlags(['flag']);
		FeatureGates.overrideConfig('rovo-search-compare-results-pass-through-rate', { cohort: 'off' });
		analyticsEvent.fire(ANALYTICS_CHANNEL);
		setIsOverrideComplete(true);
		setIsOpen(false);
	};

	const handleFlagDismissed = () => {
		setFlags(flags.slice(1));
	};

	const handleLinkOnClick = () => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'opened',
			actionSubject: 'sxsOptionsMenu',
			type: ANALYTIC_TYPE.SEND_TRACK_EVENT,
			eventType: EVENT_TYPE.TRACK,
			source: ANALYTICS_CHANNEL,
		});
		analyticsEvent.fire(ANALYTICS_CHANNEL);
	};

	return (
		<Box>
			<Popup
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				placement="bottom-end"
				content={() => (
					<Box xcss={contentStyles}>
						<MenuGroup maxWidth={800} minWidth={150}>
							<Section>
								<LinkItem
									href="https://hello.atlassian.net/wiki/x/Uo838w"
									target="_blank"
									onClick={handleLinkOnClick}
									// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx
								>
									Why am I seeing this?
								</LinkItem>
								{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
								<ButtonItem onClick={handleButtonClick}>Don’t show this again</ButtonItem>
							</Section>
						</MenuGroup>
					</Box>
				)}
				trigger={(triggerProps) =>
					fg('custom-interactive-elements-not-keyboard-focusable') ? (
						<Pressable
							xcss={moreIconStyles}
							{...triggerProps}
							onClick={() => setIsOpen(!isOpen)}
							backgroundColor="color.background.neutral.subtle"
						>
							<MoreIcon color="currentColor" spacing="spacious" label="more_icon" />
						</Pressable>
					) : (
						// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
						<Box xcss={moreIconStyles} {...triggerProps} onClick={() => setIsOpen(!isOpen)}>
							<MoreIcon color="currentColor" spacing="spacious" label="more_icon" />
						</Box>
					)
				}
				shouldRenderToParent={fg('should-render-to-parent-should-be-true-search-data')}
			/>
			{isOverrideComplete && (
				<FlagGroup
					onDismissed={() => {
						handleFlagDismissed();
					}}
				>
					{flags.map((flagId) => {
						return (
							<AutoDismissFlag
								appearance="success"
								icon={<SuccessIcon color="currentColor" spacing="spacious" label="Success" />}
								id={flagId}
								key={flagId}
								title="You have opted out. Please refresh the page."
							/>
						);
					})}
				</FlagGroup>
			)}
		</Box>
	);
};
